import type { FormulationEntry } from "@/utils/types/compound";
import type { Target } from "@/utils/types/flavor";

export type ReplacementPreview = {
  fromId: string;
  toIds: string[];
  deltaLoss?: number;
  sourceName?: string;
  replName?: string;
  isDrop?: boolean;
};

export type CurrentSensory = {
  taste: number[];
  smell: number[];
};

// FNV-1a string hash → 0..1.
function hash01(s: string): number {
  let h = 1469598103;
  for (let i = 0; i < s.length; i++) {
    h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
}

// Pseudo "current" sensory profile derived from the formulation. Mirrors the
// prototype's `currentFor` exactly so the radar shapes match.
export function computeCurrent(
  formulation: FormulationEntry[],
  target: Target | null | undefined,
  preview: ReplacementPreview | null = null
): CurrentSensory | null {
  if (!target) return null;
  // Empty formulation → no "current" sensory profile. Returning null cascades
  // to computeLoss(null, ...) → null, so the Loss readout shows the empty
  // state (no comparison can be made when nothing is in the flask).
  if (formulation.length === 0) return null;

  const totalCount = formulation.length || 1;
  const deficit = Math.max(0, 0.55 - totalCount * 0.07);

  const previewSeed = preview
    ? preview.toIds.join(",") + "|" + preview.fromId
    : "";
  const previewShiftA = preview ? (hash01(previewSeed + "a") - 0.5) * 1.2 : 0;
  const previewShiftB = preview ? (hash01(previewSeed + "b") - 0.5) * 1.2 : 0;

  const sumPpm = formulation.reduce((s, f) => s + f.ppm, 0);

  const taste = target.taste.map((v, i) => {
    const jitter = Math.sin((sumPpm + i) * 0.7) * 0.6;
    return Math.max(0, Math.min(10, v * (1 - deficit * 0.15) + jitter + previewShiftA));
  });
  const smell = target.smell.map((v, i) => {
    const jitter = Math.cos((sumPpm + i) * 0.3) * 0.7;
    return Math.max(0, Math.min(10, v * (1 - deficit * 0.2) + jitter + previewShiftB));
  });

  return { taste, smell };
}
